/**
 * Centralized Cache Manager for Apollo Client
 * 
 * This module provides a single source of truth for all cache operations related to messages.
 * Instead of duplicating cache update logic across multiple mutation hooks, all cache operations
 * are consolidated here.
 * 
 * Benefits:
 * - Single place to update when cache structure changes
 * - Consistent behavior across all operations (delete, trash, restore, move)
 * - Easier debugging and maintenance
 * - Proper handling of all cache locations (folders, starred, checkboxes, search, etc.)
 */

import { QUERY } from '../gql-operations';
import { HIERARCHY, HISTORY, STARRED, CHECKBOXES } from './specialFolderEnums';
import { DEFAULT_SORT_FIELD, HISTORY_SORT_FIELD } from '../constants/dateField';

const getFolderGroupTotalCount = (folderGroup) => {
    const totalCount = Number(folderGroup?.totalCount);
    return Number.isFinite(totalCount) ? totalCount : folderGroup?.messages?.length || 0;
};

/**
 * Remove messages from all relevant caches
 * 
 * Uses cache.evict() FIRST to remove normalized messages, then cache.modify() 
 * to directly update query results. This bypasses Apollo's merge policies
 * which would otherwise re-add messages during writeQuery operations.
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {Array} messages - Array of message objects to remove (must have id and optionally folder)
 * @param {Object} options - Options for the operation
 * @param {string} options.folderId - Current folder ID
 * @param {boolean} options.isTrash - Whether viewing trash messages
 * @param {Array} options.filterType - Active filter types (e.g., ['STAR', 'CHECK'])
 * @param {boolean} options.isSearch - Whether in search mode
 * @param {string} options.searchTerm - Current search term
 * @param {boolean} options.searchGlobal - Whether search is global
 */
export function removeMessagesFromCache(client, messages, options = {}) {
    const {
        folderId,
        isTrash = false,
        filterType = [],
        isSearch = false,
        searchTerm = '',
        searchGlobal = false,
        searchFolderFilterType = [],
        skipSearchRemoval = false,
        skipNormalizedEviction = false // Skip eviction when we want to keep the message in some caches
    } = options;

    const messageIds = new Set(messages.map(m => m.id));
    const cache = client.cache;

    if (process.env.NODE_ENV === 'development') {
        console.log('🗑️ CacheManager.removeMessages:', {
            count: messages.length,
            folderId,
            isTrash,
            filterType,
            skipNormalizedEviction
        });
    }

    // STEP 1: Evict messages from normalized cache FIRST
    // This must happen before any other cache operations to ensure
    // merge policies don't re-add the messages
    // Skip eviction if skipNormalizedEviction is true (e.g., for global search moves where we want to keep the message)
    if (!skipNormalizedEviction) {
        messages.forEach(message => {
            try {
                const normalizedId = cache.identify({ __typename: 'Message', id: message.id });
                if (normalizedId) {
                    cache.evict({ id: normalizedId });
                }
            } catch (error) {
                // Message might not be in normalized cache
            }
        });

        // Run garbage collection to clean up orphaned references
        cache.gc();
    }

    // STEP 2: Read from cache, extract the serialized data, and check for any 
    // references to evicted messages. Since we evicted the messages, any refs 
    // to them are now dangling. We need to clean up query results.

    const extractMessageIdFromRef = (ref) => {
        if (!ref || typeof ref !== 'string') return null;
        const refValue = ref.startsWith('Message:') ? ref.slice('Message:'.length) : ref;

        // Apollo can serialize refs as Message:{"id":"..."} or Message:...
        if (refValue.startsWith('{')) {
            try {
                return JSON.parse(refValue).id || null;
            } catch (error) {
                return refValue;
            }
        }

        return refValue;
    };
    
    // Helper to filter out evicted message refs
    const filterMessages = (existingMessages) => {
        if (!existingMessages || !Array.isArray(existingMessages)) return existingMessages;
        return existingMessages.filter(msgRef => {
            if (!msgRef) return false;
            // Handle both normalized refs and direct objects
            if (msgRef.__ref) {
                const msgId = extractMessageIdFromRef(msgRef.__ref);
                if (!msgId) return false;
                return !messageIds.has(msgId);
            }
            if (msgRef.id) {
                return !messageIds.has(msgRef.id);
            }
            return true;
        });
    };

    // Modifier for getFolderContent fields (nested messages.messages structure)
    const modifyFolderContent = (existing) => {
        if (!existing?.messages?.messages) return existing;
        const filtered = filterMessages(existing.messages.messages);
        if (filtered.length === existing.messages.messages.length) return existing;
        return {
            ...existing,
            messages: {
                ...existing.messages,
                messages: filtered
            }
        };
    };

    // Modifier for findMessages/filterMessages/searchMessages (direct messages array)
    const modifyMessagesQuery = (existing) => {
        if (!existing?.messages) return existing;
        const filtered = filterMessages(existing.messages);
        if (filtered.length === existing.messages.length) return existing;
        return {
            ...existing,
            messages: filtered
        };
    };

    // Modifier for global starred/checkbox queries (array of folder groups)
    const modifyGlobalQuery = (existing) => {
        if (!existing || !Array.isArray(existing)) return existing;
        let changed = false;
        const updated = existing
            .map(folderGroup => {
                if (!folderGroup?.messages) return folderGroup;
                const filtered = filterMessages(folderGroup.messages);
                if (filtered.length !== folderGroup.messages.length) {
                    changed = true;
                    const removedCount = folderGroup.messages.length - filtered.length;
                    return {
                        ...folderGroup,
                        messages: filtered,
                        totalCount: Math.max(
                            getFolderGroupTotalCount(folderGroup) - removedCount,
                            filtered.length
                        )
                    };
                }
                return folderGroup;
            })
            .filter(fg => fg?.messages?.length > 0);
        return changed || updated.length !== existing.length ? updated : existing;
    };

    // Get the cached data to find all field keys
    const rootQuery = cache.extract()?.ROOT_QUERY;
    if (rootQuery) {
        const fieldsToModify = {};
        
        Object.keys(rootQuery).forEach(fieldKey => {
            // Skip __typename
            if (fieldKey === '__typename') return;
            
            // Match query types by their prefix
            if (fieldKey.startsWith('getFolderContent')) {
                fieldsToModify[fieldKey] = modifyFolderContent;
            } else if (fieldKey.startsWith('findMessages') || 
                       fieldKey.startsWith('filterMessages') || 
                       (fieldKey.startsWith('searchMessages') && !skipSearchRemoval)) {
                fieldsToModify[fieldKey] = modifyMessagesQuery;
            } else if (fieldKey === 'getStarredMessagesGlobal' || 
                       fieldKey === 'getCheckboxMessagesGlobal') {
                fieldsToModify[fieldKey] = modifyGlobalQuery;
            }
        });

        // Apply all modifications at once
        if (Object.keys(fieldsToModify).length > 0) {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔧 Modifying cache fields:', Object.keys(fieldsToModify));
            }
            try {
                cache.modify({
                    id: 'ROOT_QUERY',
                    fields: fieldsToModify,
                    broadcast: true // Ensure watchers are notified
                });
                if (process.env.NODE_ENV === 'development') {
                    console.log('✅ cache.modify completed successfully');
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('cache.modify error:', error.message);
                }
            }
        } else {
            if (process.env.NODE_ENV === 'development') {
                console.log('⚠️ No cache fields found to modify');
            }
        }
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('✅ CacheManager.removeMessages completed');
    }
}

/**
 * Update messages in cache (for move operations - update folder reference)
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {Array} messages - Array of message objects to update
 * @param {Object} updates - Updates to apply to each message
 * @param {Object} options - Options for the operation
 */
export function updateMessagesInCache(client, messages, updates, options = {}) {
    const {
        folderId,
        isTrash = false,
        filterType = [],
        toFolder,
        searchTerm = ''
    } = options;
    const normalizedFilterSearchText = String(searchTerm || '').trim();

    const cache = client.cache;
    const messageIds = new Set(messages.map(m => m.id));

    if (process.env.NODE_ENV === 'development') {
        console.log('📝 CacheManager.updateMessages:', {
            count: messages.length,
            folderId,
            toFolder: toFolder?.id
        });
    }

    // For move operations: Remove from source folder
    if (toFolder && folderId && folderId !== toFolder.id) {
        // Remove from source folder cache
        if (folderId !== HIERARCHY && folderId !== STARRED && folderId !== CHECKBOXES) {
            try {
                const sourceVariables = {
                    data: {
                        folderId,
                        isTrash
                    }
                };
                const sourceData = cache.readQuery({
                    query: QUERY.GET_FOLDER_CONTENT,
                    variables: sourceVariables
                });

                if (sourceData?.getFolderContent?.messages?.messages) {
                    const newMessages = sourceData.getFolderContent.messages.messages.filter(
                        msg => msg && !messageIds.has(msg.id)
                    );

                    cache.writeQuery({
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables: sourceVariables,
                        data: {
                            getFolderContent: {
                                ...sourceData.getFolderContent,
                                messages: {
                                    ...sourceData.getFolderContent.messages,
                                    messages: newMessages
                                }
                            }
                        }
                    });
                }
            } catch (error) {
                // Cache entry doesn't exist, skip
            }
        }

        // Add to destination folder cache
        try {
            const destVariables = {
                data: {
                    folderId: toFolder.id,
                    isTrash
                }
            };
            const destData = cache.readQuery({
                query: QUERY.GET_FOLDER_CONTENT,
                variables: destVariables
            });

            if (destData?.getFolderContent?.messages?.messages) {
                // Update messages with new folder reference
                const movedMessages = messages.map(msg => ({
                    ...msg,
                    folder: toFolder,
                    createdAt: new Date().toISOString() // Move to top of list
                }));

                const existingIds = new Set(destData.getFolderContent.messages.messages.map(m => m?.id));
                const newMessages = [
                    ...movedMessages.filter(m => !existingIds.has(m.id)),
                    ...destData.getFolderContent.messages.messages
                ];

                cache.writeQuery({
                    query: QUERY.GET_FOLDER_CONTENT,
                    variables: destVariables,
                    data: {
                        getFolderContent: {
                            ...destData.getFolderContent,
                            messages: {
                                ...destData.getFolderContent.messages,
                                messages: newMessages
                            }
                        }
                    }
                });
            }
        } catch (error) {
            // Destination folder not in cache, will be loaded when user navigates there
        }

        // Update in FIND_MESSAGES (All folder)
        const sortField = DEFAULT_SORT_FIELD;
        try {
            const findVariables = {
                data: {
                    text: '',
                    folderId: '',
                    isTrash,
                    sortBy: sortField
                }
            };
            const findData = cache.readQuery({
                query: QUERY.FIND_MESSAGES,
                variables: findVariables
            });

            if (findData?.findMessages?.messages) {
                const newMessages = findData.findMessages.messages.map(msg => {
                    if (msg && messageIds.has(msg.id)) {
                        return {
                            ...msg,
                            folder: toFolder
                        };
                    }
                    return msg;
                });

                cache.writeQuery({
                    query: QUERY.FIND_MESSAGES,
                    variables: findVariables,
                    data: {
                        findMessages: {
                            ...findData.findMessages,
                            messages: newMessages
                        }
                    }
                });
            }
        } catch (error) {
            // Cache entry doesn't exist, skip
        }

        // Update in FILTER_MESSAGES if filters are active
        if (filterType && filterType.length > 0) {
            try {
                const filterVariables = {
                    data: {
                        text: normalizedFilterSearchText,
                        type: filterType,
                        folderId: folderId === HIERARCHY ? '' : folderId,
                        isTrash
                    }
                };
                const filterData = cache.readQuery({
                    query: QUERY.FILTER_MESSAGES,
                    variables: filterVariables
                });

                if (filterData?.filterMessages?.messages) {
                    // For filtered view in source folder, remove messages
                    const newMessages = filterData.filterMessages.messages.filter(
                        msg => msg && !messageIds.has(msg.id)
                    );

                    cache.writeQuery({
                        query: QUERY.FILTER_MESSAGES,
                        variables: filterVariables,
                        data: {
                            filterMessages: {
                                ...filterData.filterMessages,
                                messages: newMessages
                            }
                        }
                    });
                }
            } catch (error) {
                // Cache entry doesn't exist, skip
            }
        }

        // Update starred messages cache
        updateStarredCacheForMove(cache, messages, toFolder);

        // Update checkbox messages cache
        updateCheckboxCacheForMove(cache, messages, toFolder);
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('✅ CacheManager.updateMessages completed');
    }
}

/**
 * Helper to update starred messages cache when moving messages
 */
function updateStarredCacheForMove(cache, messages, toFolder) {
    try {
        const starredData = cache.readQuery({
            query: QUERY.GET_STARRED_MESSAGES_GLOBAL
        });

        if (!starredData?.getStarredMessagesGlobal) return;

        const starredMessages = messages.filter(m => m.isStarred);
        if (starredMessages.length === 0) return;

        const starredMessageIds = new Set(starredMessages.map(m => m.id));
        let updatedFolders = JSON.parse(JSON.stringify(starredData.getStarredMessagesGlobal));

        // Remove from source folder groups
        updatedFolders = updatedFolders.map(folderGroup => {
            if (!folderGroup.messages) return folderGroup;
            
            const newMessages = folderGroup.messages.filter(
                msg => msg && !starredMessageIds.has(msg.id)
            );
            
            return {
                ...folderGroup,
                messages: newMessages
            };
        }).filter(folderGroup => folderGroup.messages && folderGroup.messages.length > 0);

        // Add to destination folder group
        const destFolderIndex = updatedFolders.findIndex(fg => fg.folder?.id === toFolder.id);
        const movedStarredMessages = starredMessages.map(msg => ({
            ...msg,
            folder: toFolder,
            createdAt: new Date().toISOString()
        }));

        if (destFolderIndex >= 0) {
            updatedFolders[destFolderIndex].messages = [
                ...movedStarredMessages,
                ...updatedFolders[destFolderIndex].messages
            ];
        } else {
            updatedFolders.unshift({
                __typename: 'FolderContentGlobal',
                folder: toFolder,
                messages: movedStarredMessages
            });
        }

        cache.writeQuery({
            query: QUERY.GET_STARRED_MESSAGES_GLOBAL,
            data: {
                getStarredMessagesGlobal: updatedFolders
            }
        });
    } catch (error) {
        // Cache entry doesn't exist, skip
    }
}

/**
 * Helper to update checkbox messages cache when moving messages
 */
function updateCheckboxCacheForMove(cache, messages, toFolder) {
    try {
        const checkboxData = cache.readQuery({
            query: QUERY.GET_CHECKBOX_MESSAGES_GLOBAL
        });

        if (!checkboxData?.getCheckboxMessagesGlobal) return;

        const checkboxMessages = messages.filter(m => m.isCheckbox);
        if (checkboxMessages.length === 0) return;

        const checkboxMessageIds = new Set(checkboxMessages.map(m => m.id));
        let updatedFolders = JSON.parse(JSON.stringify(checkboxData.getCheckboxMessagesGlobal));

        // Remove from source folder groups
        updatedFolders = updatedFolders.map(folderGroup => {
            if (!folderGroup.messages) return folderGroup;
            
            const newMessages = folderGroup.messages.filter(
                msg => msg && !checkboxMessageIds.has(msg.id)
            );
            const removedCount = folderGroup.messages.length - newMessages.length;
            
            return {
                ...folderGroup,
                messages: newMessages,
                totalCount: Math.max(
                    getFolderGroupTotalCount(folderGroup) - removedCount,
                    newMessages.length
                )
            };
        }).filter(folderGroup => folderGroup.messages && folderGroup.messages.length > 0);

        // Add to destination folder group
        const destFolderIndex = updatedFolders.findIndex(fg => fg.folder?.id === toFolder.id);
        const movedCheckboxMessages = checkboxMessages.map(msg => ({
            ...msg,
            folder: toFolder,
            createdAt: new Date().toISOString()
        }));

        if (destFolderIndex >= 0) {
            const existingTotalCount = getFolderGroupTotalCount(updatedFolders[destFolderIndex]);
            updatedFolders[destFolderIndex].messages = [
                ...movedCheckboxMessages,
                ...updatedFolders[destFolderIndex].messages
            ];
            updatedFolders[destFolderIndex].totalCount =
                existingTotalCount + movedCheckboxMessages.length;
        } else {
            updatedFolders.unshift({
                __typename: 'FolderContentGlobal',
                folder: toFolder,
                messages: movedCheckboxMessages,
                totalCount: movedCheckboxMessages.length
            });
        }

        cache.writeQuery({
            query: QUERY.GET_CHECKBOX_MESSAGES_GLOBAL,
            data: {
                getCheckboxMessagesGlobal: updatedFolders
            }
        });
    } catch (error) {
        // Cache entry doesn't exist, skip
    }
}

/**
 * Add messages to a destination folder cache (for move operations)
 * This is used after removeMessages to add the messages to their new folder.
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {Array} messages - Array of message objects to add
 * @param {Object} toFolder - Destination folder object
 * @param {boolean} isTrash - Whether in trash context
 */
export function addMessagesToFolder(client, messages, toFolder, isTrash = false, threadVisibilityFilter = undefined) {
    if (!toFolder?.id || !messages?.length) return;

    const cache = client.cache;
    const filterMessagesForVisibility = (sourceMessages, includeThreadMessages, includeChatMessages) =>
        sourceMessages.filter(message => {
            if (!message?.parentMessageId) {
                return true;
            }

            if (message?.laneKind === 'AI') {
                return includeChatMessages;
            }

            return includeThreadMessages;
        });

    const includeThreadMessages = threadVisibilityFilter?.includeThreadMessages !== false;
    const includeChatMessages = threadVisibilityFilter?.includeChatMessages !== false;
    const visibleMessages = filterMessagesForVisibility(messages, includeThreadMessages, includeChatMessages);

    const writeMessagesIntoFolderCache = (destVariables, scopedVisibleMessages) => {
        if (!Array.isArray(scopedVisibleMessages) || scopedVisibleMessages.length === 0) {
            return false;
        }

        try {
            const destData = cache.readQuery({
                query: QUERY.GET_FOLDER_CONTENT,
                variables: destVariables
            });

            if (!destData?.getFolderContent?.messages?.messages) {
                return false;
            }

            const movedMessages = scopedVisibleMessages.map(msg => ({
                ...msg,
                folder: toFolder
            }));

            const existingIds = new Set(destData.getFolderContent.messages.messages.map(m => m?.id));
            const newMessages = [
                ...movedMessages.filter(m => !existingIds.has(m.id)),
                ...destData.getFolderContent.messages.messages
            ];

            newMessages.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));

            cache.writeQuery({
                query: QUERY.GET_FOLDER_CONTENT,
                variables: destVariables,
                data: {
                    getFolderContent: {
                        ...destData.getFolderContent,
                        messages: {
                            ...destData.getFolderContent.messages,
                            messages: newMessages
                        }
                    }
                }
            });

            return true;
        } catch (error) {
            return false;
        }
    };

    const buildDestinationVariables = () => {
        const candidates = [];
        const seen = new Set();

        const pushCandidate = data => {
            const key = JSON.stringify(data);
            if (seen.has(key)) return;
            seen.add(key);
            candidates.push({ data });
            // FolderContainer always supplies this key, including false for lists.
            // Board rank-ordered pages have their own cache entry as well.
            [false, true].forEach(respectManualOrder => {
                candidates.push({ data: { ...data, respectManualOrder } });
            });
        };

        pushCandidate({
            folderId: toFolder.id,
            isTrash,
            ...(threadVisibilityFilter
                ? {
                      includeThreadMessages,
                      includeChatMessages
                  }
                : {})
        });

        // Update all common visibility cache variants so destination list updates immediately
        // regardless of which variant is currently mounted.
        [
            [true, true],
            [true, false],
            [false, true],
            [false, false]
        ].forEach(([threadVisible, chatVisible]) => {
            pushCandidate({
                folderId: toFolder.id,
                isTrash,
                includeThreadMessages: threadVisible,
                includeChatMessages: chatVisible
            });
        });

        // Legacy/non-filtered cache key shape.
        pushCandidate({
            folderId: toFolder.id,
            isTrash
        });

        return candidates;
    };

    if (process.env.NODE_ENV === 'development') {
        console.log('➕ CacheManager.addMessagesToFolder:', {
            count: messages.length,
            visibleCount: visibleMessages.length,
            toFolderId: toFolder.id
        });
    }

    const destVariableCandidates = buildDestinationVariables();
    destVariableCandidates.forEach(destVariables => {
        const candidateIncludeThread =
            destVariables?.data?.includeThreadMessages !== undefined
                ? destVariables.data.includeThreadMessages
                : includeThreadMessages;
        const candidateIncludeChat =
            destVariables?.data?.includeChatMessages !== undefined
                ? destVariables.data.includeChatMessages
                : includeChatMessages;
        const candidateVisibleMessages = filterMessagesForVisibility(
            messages,
            candidateIncludeThread !== false,
            candidateIncludeChat !== false
        );
        writeMessagesIntoFolderCache(destVariables, candidateVisibleMessages);
    });

    // Update starred messages cache if any moved messages are starred
    const starredMessages = messages.filter(m => m.isStarred);
    if (starredMessages.length > 0) {
        try {
            const starredData = cache.readQuery({
                query: QUERY.GET_STARRED_MESSAGES_GLOBAL
            });

            if (starredData?.getStarredMessagesGlobal) {
                let updatedFolders = JSON.parse(JSON.stringify(starredData.getStarredMessagesGlobal));
                
                const movedStarredMessages = starredMessages.map(msg => ({
                    ...msg,
                    folder: toFolder
                }));

                // Find or create destination folder group
                const destFolderIndex = updatedFolders.findIndex(fg => fg.folder?.id === toFolder.id);
                
                if (destFolderIndex >= 0) {
                    const existingIds = new Set(updatedFolders[destFolderIndex].messages.map(m => m?.id));
                    updatedFolders[destFolderIndex].messages = [
                        ...movedStarredMessages.filter(m => !existingIds.has(m.id)),
                        ...updatedFolders[destFolderIndex].messages
                    ];
                } else {
                    updatedFolders.unshift({
                        __typename: 'FolderContentGlobal',
                        folder: toFolder,
                        messages: movedStarredMessages
                    });
                }

                cache.writeQuery({
                    query: QUERY.GET_STARRED_MESSAGES_GLOBAL,
                    data: {
                        getStarredMessagesGlobal: updatedFolders
                    }
                });
            }
        } catch (error) {
            // Starred cache doesn't exist, skip
        }
    }

    // Update checkbox messages cache if any moved messages have checkboxes
    const checkboxMessages = messages.filter(m => m.isCheckbox);
    if (checkboxMessages.length > 0) {
        try {
            const checkboxData = cache.readQuery({
                query: QUERY.GET_CHECKBOX_MESSAGES_GLOBAL
            });

            if (checkboxData?.getCheckboxMessagesGlobal) {
                let updatedFolders = JSON.parse(JSON.stringify(checkboxData.getCheckboxMessagesGlobal));
                
                const movedCheckboxMessages = checkboxMessages.map(msg => ({
                    ...msg,
                    folder: toFolder
                }));

                // Find or create destination folder group
                const destFolderIndex = updatedFolders.findIndex(fg => fg.folder?.id === toFolder.id);
                
                if (destFolderIndex >= 0) {
                    const existingIds = new Set(updatedFolders[destFolderIndex].messages.map(m => m?.id));
                    const existingTotalCount = getFolderGroupTotalCount(updatedFolders[destFolderIndex]);
                    const messagesToAdd = movedCheckboxMessages.filter(m => !existingIds.has(m.id));
                    updatedFolders[destFolderIndex].messages = [
                        ...messagesToAdd,
                        ...updatedFolders[destFolderIndex].messages
                    ];
                    updatedFolders[destFolderIndex].totalCount =
                        existingTotalCount + messagesToAdd.length;
                } else {
                    updatedFolders.unshift({
                        __typename: 'FolderContentGlobal',
                        folder: toFolder,
                        messages: movedCheckboxMessages,
                        totalCount: movedCheckboxMessages.length
                    });
                }

                cache.writeQuery({
                    query: QUERY.GET_CHECKBOX_MESSAGES_GLOBAL,
                    data: {
                        getCheckboxMessagesGlobal: updatedFolders
                    }
                });
            }
        } catch (error) {
            // Checkbox cache doesn't exist, skip
        }
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('✅ CacheManager.addMessagesToFolder completed');
    }
}

/**
 * Add a message to the cache
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {Object} message - Message object to add
 * @param {Object} options - Options for the operation
 */
export function addMessageToCache(client, message, options = {}) {
    const {
        folderId,
        isTrash = false
    } = options;

    const cache = client.cache;

    if (process.env.NODE_ENV === 'development') {
        console.log('➕ CacheManager.addMessage:', {
            messageId: message.id,
            folderId
        });
    }

    // Add to folder cache
    if (folderId && folderId !== HIERARCHY) {
        try {
            const variables = {
                data: {
                    folderId,
                    isTrash
                }
            };
            const data = cache.readQuery({
                query: QUERY.GET_FOLDER_CONTENT,
                variables
            });

            if (data?.getFolderContent?.messages?.messages) {
                // Check if message already exists
                const exists = data.getFolderContent.messages.messages.some(
                    msg => msg?.id === message.id
                );

                if (!exists) {
                    const newMessages = [message, ...data.getFolderContent.messages.messages];

                    cache.writeQuery({
                        query: QUERY.GET_FOLDER_CONTENT,
                        variables,
                        data: {
                            getFolderContent: {
                                ...data.getFolderContent,
                                messages: {
                                    ...data.getFolderContent.messages,
                                    messages: newMessages
                                }
                            }
                        }
                    });
                }
            }
        } catch (error) {
            // Cache entry doesn't exist, skip
        }
    }

    // Add to FIND_MESSAGES (All folder)
    try {
        const findVariables = {
            data: {
                text: '',
                folderId: '',
                isTrash,
                sortBy: DEFAULT_SORT_FIELD
            }
        };
        const findData = cache.readQuery({
            query: QUERY.FIND_MESSAGES,
            variables: findVariables
        });

        if (findData?.findMessages?.messages) {
            const exists = findData.findMessages.messages.some(
                msg => msg?.id === message.id
            );

            if (!exists) {
                const newMessages = [message, ...findData.findMessages.messages];

                cache.writeQuery({
                    query: QUERY.FIND_MESSAGES,
                    variables: findVariables,
                    data: {
                        findMessages: {
                            ...findData.findMessages,
                            messages: newMessages
                        }
                    }
                });
            }
        }
    } catch (error) {
        // Cache entry doesn't exist, skip
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('✅ CacheManager.addMessage completed');
    }
}

/**
 * Update a single message in the cache
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {string} messageId - ID of the message to update
 * @param {Object} updates - Updates to apply
 */
export function updateSingleMessageInCache(client, messageId, updates) {
    const cache = client.cache;

    if (process.env.NODE_ENV === 'development') {
        console.log('📝 CacheManager.updateSingleMessage:', {
            messageId,
            updates: Object.keys(updates)
        });
    }

    // Use cache.modify to update the normalized message directly
    // This is the most efficient way to update a message across all queries
    try {
        const normalizedId = cache.identify({ __typename: 'Message', id: messageId });
        if (normalizedId) {
            cache.modify({
                id: normalizedId,
                fields: Object.fromEntries(
                    Object.entries(updates).map(([key, value]) => [
                        key,
                        () => value
                    ])
                )
            });
        }
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.log('Could not modify normalized message:', error);
        }
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('✅ CacheManager.updateSingleMessage completed');
    }
}

/**
 * Evict a message completely from the cache
 * 
 * @param {ApolloClient} client - Apollo client instance
 * @param {string} messageId - ID of the message to evict
 */
export function evictMessageFromCache(client, messageId) {
    const cache = client.cache;

    try {
        const normalizedId = cache.identify({ __typename: 'Message', id: messageId });
        if (normalizedId) {
            cache.evict({ id: normalizedId });
            cache.gc();
        }
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.log('Could not evict message:', error);
        }
    }
}

/**
 * Dispatch event to update UI components that listen for cache changes
 * This is used to update components that maintain their own state in addition to Apollo cache
 */
export function dispatchCacheUpdateEvent(eventName, detail) {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    }
}

// Export all functions as a namespace for convenience
export const CacheManager = {
    removeMessages: removeMessagesFromCache,
    updateMessages: updateMessagesInCache,
    addMessage: addMessageToCache,
    addMessagesToFolder: addMessagesToFolder,
    updateSingleMessage: updateSingleMessageInCache,
    evictMessage: evictMessageFromCache,
    dispatchEvent: dispatchCacheUpdateEvent
};

export default CacheManager;
